import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { encryptData, decryptData } from "../../utils/localEncruption";
import { decryptAPIData } from "../../utils/localEncruption";

// Helper function to fetch data from an API
const fetchDataFromAPI = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(
      encryptedData,
      process.env.REACT_APP_ENCRYPTION_KEY ||
      "gsDFQwTwuuOXWgh0JG/En4NtZWbahbacdQtlCkMg9tE=",
    );
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for FCNR rate:", error);
  }

  return response.json();
};

// Helper function to get data from local storage
const getDataFromLocalStorage = (key) => {
  const storedData = localStorage.getItem(key);
  const salt = `${process.env.REACT_APP_SALT}`;

  if (!storedData) {
    return null;
  }

  try {
    const decryptedData = decryptData(storedData, salt);
    return decryptedData;
  } catch (error) {
    return null;
  }
};

// Helper function to save data to local storage
const saveDataToLocalStorage = (key, data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);

  localStorage.setItem(key, encryptedData);
};

// Helper function to get the timestamp when data was last fetched
const getTimestampFromLocalStorage = (key) => {
  const timestamp = localStorage.getItem(`${key}_timestamp`);
  return timestamp ? parseInt(timestamp, 10) : null;
};

// Helper function to save the timestamp when data is fetched
const saveTimestampToLocalStorage = (key) => {
  const timestamp = new Date().getTime();
  localStorage.setItem(`${key}_timestamp`, timestamp);
};

// Helper function to clear stale data from localStorage
const clearStaleDataFromLocalStorage = () => {
  const fcnrKeys = [];

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);

    if (
      key === "fcnrData" ||
      key === "fcnrData_timestamp" ||
      key?.startsWith("fcnrData_")
    ) {
      fcnrKeys.push(key);
    }
  }

  fcnrKeys.forEach((key) => localStorage.removeItem(key));
};

// Always revalidate against the backend; localStorage is only an API-error fallback.
const DATA_FRESHNESS_THRESHOLD = 0;

// Async thunk to fetch FCNR rates — accepts currency as parameter
export const fetchFcnrRates = createAsyncThunk(
  "fcnrRateData/fetchFcnrRates",
  async (currency = "USD", thunkAPI) => {
    const cacheKey = `fcnrData_${currency}`;
    const timestamp = getTimestampFromLocalStorage(cacheKey);
    const currentTime = new Date().getTime();

    // Check if data in local storage is stale
    const isDataStale =
      !timestamp || currentTime - timestamp >= DATA_FRESHNESS_THRESHOLD;

    // If data is NOT stale, try to use cached data
    if (!isDataStale) {
      const cachedData = getDataFromLocalStorage(cacheKey);

      if (cachedData) {
        console.log(`Using cached FCNR rates data for ${currency}`);
        return { data: cachedData, currency };
      }

      console.log("Cached FCNR data incomplete, fetching fresh data");
    } else {
      console.log("Cached FCNR data is stale, fetching fresh data");
    }

    // Fetch fresh data from API
    try {
      const baseUrl = process.env.REACT_APP_VIKALP || 'http://127.0.0.1:8000';
      const url = `${baseUrl}/fcnrrate_interest/?currency=${currency}`;
      const data = await fetchDataFromAPI(url);

      // Save fetched data and timestamps to local storage
      saveDataToLocalStorage(cacheKey, data);
      saveTimestampToLocalStorage(cacheKey);

      console.log(`Fresh FCNR rates data fetched for ${currency}`);
      return { data, currency };
    } catch (error) {
      console.error("Failed to fetch FCNR rates from API:", error);

      // On error, try to return cached data even if stale (fallback)
      const cachedData = getDataFromLocalStorage(cacheKey);

      if (cachedData) {
        console.warn("API fetch failed, using stale cached data as fallback");
        return { data: cachedData, currency };
      }

      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Async thunk to fetch specific bank details for FCNR
export const fetchFcnrBankDetails = createAsyncThunk(
  "fcnrRateData/fetchFcnrBankDetails",
  async (bankName, thunkAPI) => {
    try {
      const baseUrl = process.env.REACT_APP_VIKALP || 'http://127.0.0.1:8000';
      const targetUrl = `${baseUrl}/fcnr-bank-details/${encodeURIComponent(
        bankName,
      )}/`;
      const data = await fetchDataFromAPI(targetUrl);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Define the initial state
const initialState = {
  isLoading: false,
  data: {},               // { top_fcnr: [...], all_fcnr: {...} }
  isError: false,
  selectedBankDetails: null, // Store details for the selected FCNR bank
  isDetailsLoading: false,
  detailsError: null,
  selectedCurrency: "USD",  // Currency filter
  selectedFilters: [],       // Bank type filters
  selectedTenor: "All terms",
  selectedBanks: [],
  sliderState: {
    year: 0,
    month: 0,
    sliderRange: 0,
    isActive: true,
  },
};

// Create slice
const fcnrRateDataSlice = createSlice({
  name: "fcnrRateData",
  initialState,

  reducers: {
    setSelectedCurrency: (state, action) => {
      state.selectedCurrency = action.payload;
    },

    toggleFilterFCNR: (state, action) => {
      const selectedCategory = action.payload;
      if (state.selectedFilters.includes(selectedCategory)) {
        state.selectedFilters = state.selectedFilters.filter(
          (element) => element !== selectedCategory,
        );
      } else {
        state.selectedFilters.push(selectedCategory);
      }
    },
    resetFiltersFCNR: (state) => {
      state.selectedFilters = [];
    },
    selectAllFiltersFCNR: (state, action) => {
      state.selectedFilters = action.payload;
    },

    setSelectedTenorFCNR: (state, action) => {
      state.selectedTenor = action.payload;
    },

    setSliderYearFCNR: (state, action) => {
      state.sliderState.year = action.payload;
    },

    setSliderMonthFCNR: (state, action) => {
      state.sliderState.month = action.payload;
    },

    setSliderRangeFCNR: (state, action) => {
      state.sliderState.sliderRange = action.payload;
    },

    resetSliderDataFCNR: (state) => {
      state.sliderState.sliderRange = 0;
      state.sliderState.year = 0;
      state.sliderState.month = 0;
      state.sliderState.isActive = true;
    },

    setIsActiveFCNR: (state, action) => {
      state.sliderState.isActive = action.payload;
    },
    setSelectedBanksFCNR: (state, action) => {
      state.selectedBanks = action.payload;
    },

    // Sets a preset tenor and clears custom slider data
    setPresetTenorFCNR: (state, action) => {
      state.selectedTenor = action.payload;
      state.sliderState.year = 0;
      state.sliderState.month = 0;
      state.sliderState.sliderRange = 0;
      state.sliderState.isActive = true;
    },

    // Sets custom slider values and clears preset tenor
    setCustomTenureFCNR: (state, action) => {
      const { year, month, sliderRange } = action.payload;
      state.sliderState.year = year;
      state.sliderState.month = month;
      state.sliderState.sliderRange = sliderRange;
      state.sliderState.isActive = false;
      state.selectedTenor = "";
    },

    clearFcnrRatesCache: (state) => {
      clearStaleDataFromLocalStorage();
      state.data = {};
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchFcnrRates.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(fetchFcnrRates.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload.data;
    });
    builder.addCase(fetchFcnrRates.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      console.error("Failed to fetch FCNR rates:", action.payload);
    });

    builder.addCase(fetchFcnrBankDetails.pending, (state) => {
      state.isDetailsLoading = true;
      state.detailsError = null;
    });
    builder.addCase(fetchFcnrBankDetails.fulfilled, (state, action) => {
      state.isDetailsLoading = false;
      state.selectedBankDetails = action.payload;
    });
    builder.addCase(fetchFcnrBankDetails.rejected, (state, action) => {
      state.isDetailsLoading = false;
      state.detailsError = action.payload;
    });
  },
});

export const {
  setSelectedCurrency,
  toggleFilterFCNR,
  resetFiltersFCNR,
  selectAllFiltersFCNR,
  setSelectedTenorFCNR,
  setSliderYearFCNR,
  setSliderMonthFCNR,
  setSliderRangeFCNR,
  resetSliderDataFCNR,
  setIsActiveFCNR,
  setSelectedBanksFCNR,
  setPresetTenorFCNR,
  setCustomTenureFCNR,
  clearFcnrRatesCache,
} = fcnrRateDataSlice.actions;
export default fcnrRateDataSlice.reducer;
